import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  ReactNode,
} from 'react';
import { CASHIER_AUTH_TOKEN_CHANGED_EVENT, STORAGE_KEYS } from '../constants';
import { fetchCashierMe } from '../services/cashierApi';
import type { DeviceView } from '../services/cashierApi';

export type CashierProfile = {
  id: string;
  shopId: string;
  username: string;
  status: string;
  lastLoginAt?: string | null;
};

interface AuthState {
  cashier: CashierProfile | null;
  token: string | null;
  /** The device bound to this session by the server (null = not recognised / old session). */
  device: DeviceView | null;
  isAuthenticated: boolean;
  isLoading: boolean;
}

interface AuthContextValue extends AuthState {
  login: (cashier: CashierProfile, token: string, device?: DeviceView | null) => void;
  logout: () => void;
  /** GET /cashier/me → updates cashier + device. Resolves true when the refresh was applied. */
  refreshMe: () => Promise<boolean>;
  setDevice: (device: DeviceView | null) => void;
}

const CashierAuthContext = createContext<AuthContextValue | undefined>(undefined);

function readStoredDevice(): DeviceView | null {
  const raw = localStorage.getItem(STORAGE_KEYS.CASHIER_DEVICE);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as DeviceView | null;
    return parsed && typeof parsed === 'object' && typeof parsed.id === 'string' ? parsed : null;
  } catch {
    return null;
  }
}

function storeDevice(device: DeviceView | null): void {
  if (device) {
    localStorage.setItem(STORAGE_KEYS.CASHIER_DEVICE, JSON.stringify(device));
  } else {
    localStorage.removeItem(STORAGE_KEYS.CASHIER_DEVICE);
  }
}

export function CashierAuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({
    cashier: null,
    token: null,
    device: null,
    isAuthenticated: false,
    isLoading: true,
  });

  const logout = useCallback(() => {
    localStorage.removeItem(STORAGE_KEYS.CASHIER_AUTH_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.CASHIER_DATA);
    localStorage.removeItem(STORAGE_KEYS.CASHIER_DEVICE);
    localStorage.removeItem(STORAGE_KEYS.CASHIER_DEVICE_ID);
    setState({
      cashier: null,
      token: null,
      device: null,
      isAuthenticated: false,
      isLoading: false,
    });
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent(CASHIER_AUTH_TOKEN_CHANGED_EVENT));
    }
  }, []);

  const refreshMe = useCallback(async (): Promise<boolean> => {
    const token = localStorage.getItem(STORAGE_KEYS.CASHIER_AUTH_TOKEN);
    if (!token) return false;
    const res = await fetchCashierMe();
    if (!res.success || !res.data) return false;
    // Signed out (or in as someone else) while the request was running: drop the stale answer.
    if (localStorage.getItem(STORAGE_KEYS.CASHIER_AUTH_TOKEN) !== token) return false;
    const cashier = res.data.cashier || null;
    const device = res.data.device || null;
    if (cashier) localStorage.setItem(STORAGE_KEYS.CASHIER_DATA, JSON.stringify(cashier));
    storeDevice(device);
    setState((p) => ({ ...p, cashier: cashier || p.cashier, device }));
    return true;
  }, []);

  const setDevice = useCallback((device: DeviceView | null) => {
    storeDevice(device);
    setState((p) => ({ ...p, device }));
  }, []);

  useEffect(() => {
    const token = localStorage.getItem(STORAGE_KEYS.CASHIER_AUTH_TOKEN);
    const raw = localStorage.getItem(STORAGE_KEYS.CASHIER_DATA);
    if (token && raw) {
      try {
        const cashier = JSON.parse(raw) as CashierProfile;
        setState({
          cashier,
          token,
          device: readStoredDevice(),
          isAuthenticated: true,
          isLoading: false,
        });
      } catch {
        logout();
        return;
      }
      // The cached device may be stale (approved / blocked since): ask the server.
      void refreshMe();
      return;
    }
    setState((p) => ({ ...p, isLoading: false }));
  }, [logout, refreshMe]);

  const login = useCallback((cashier: CashierProfile, token: string, device: DeviceView | null = null) => {
    localStorage.setItem(STORAGE_KEYS.CASHIER_AUTH_TOKEN, token);
    localStorage.setItem(STORAGE_KEYS.CASHIER_DATA, JSON.stringify(cashier));
    storeDevice(device);
    localStorage.removeItem(STORAGE_KEYS.CASHIER_DEVICE_ID);
    setState({
      cashier,
      token,
      device,
      isAuthenticated: true,
      isLoading: false,
    });
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent(CASHIER_AUTH_TOKEN_CHANGED_EVENT));
    }
  }, []);

  useEffect(() => {
    const onUnauthorized = () => logout();
    window.addEventListener('cashier-auth:unauthorized', onUnauthorized);
    return () => window.removeEventListener('cashier-auth:unauthorized', onUnauthorized);
  }, [logout]);

  // Coming back to the app (tab focus, Android app resumed): refresh the device status.
  useEffect(() => {
    if (!state.isAuthenticated) return;
    const onFocus = () => {
      void refreshMe();
    };
    const onVisibility = () => {
      if (document.visibilityState === 'visible') void refreshMe();
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [state.isAuthenticated, refreshMe]);

  return (
    <CashierAuthContext.Provider value={{ ...state, login, logout, refreshMe, setDevice }}>
      {children}
    </CashierAuthContext.Provider>
  );
}

export function useCashierAuth(): AuthContextValue {
  const ctx = useContext(CashierAuthContext);
  if (!ctx) throw new Error('useCashierAuth must be used within CashierAuthProvider');
  return ctx;
}
